import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Moment } from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { flatMap } from 'rxjs/operators';

import { apiLandingProduct, apiPayments, apiPartners } from '../config/api.config';
import { LanguageModel } from '../models/language.model';
import { ProductModel, ProductModelWrapper } from '../models/product.model';
import { IntervalModel } from '../models/interval.models';
import { Language } from '../models/enums/language.enum';
import { OrderModel, OrderResultModel } from '../models/order.model';
import { NavigationService } from './navigation.service';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  private currency = '';
  private productIds: number[] = [];
  private contentLanguage: Language;
  private productIdsSubject = new Subject<number[]>();
  private productsSubject = new Subject<ProductModel[]>();
  private filterSubject: BehaviorSubject<{ begin: Moment; end: Moment }>;
  private products: ProductModel[];
  private interval: IntervalModel;

  constructor(private http: HttpClient, private navigationService: NavigationService) {
    this.productIdsSubject.pipe(flatMap(() => this.loadProducts())).subscribe(
      (productWrappers: ProductModelWrapper[]) => {
        productWrappers.forEach((x) => {
          x.product.partnerId = x.partnerId;
          x.product.paymentSystemId = x.paymentSystemId;
        });
        this.products = productWrappers.map((x) => x.product);
        this.productsSubject.next(this.products);
      },
      () => {
        this.navigationService.goToNotFound();
      }
    );
  }

  private loadProducts(): Observable<ProductModelWrapper[]> {
    return forkJoin(
      this.productIds.map((productId) => {
        const url = `${apiLandingProduct}/${productId}`;
        const params = { widgetLanguage: this.contentLanguage };

        return this.http.get<ProductModelWrapper>(url, { params });
      })
    );
  }

  public getPartnerCustomization(id: number): Observable<any> {
    return this.http.get(`${apiPartners}/${id}/customisation`);
  }

  public getLanguages(): Observable<LanguageModel[]> {
    return this.http.get<LanguageModel[]>(`assets/languages.json`);
  }

  public getCurrency(): string {
    return this.currency;
  }

  public placeOrder(payload: OrderModel): Promise<OrderResultModel> {
    return this.http.post<OrderResultModel>(`${apiPayments}/place-widget-order`, payload).toPromise();
  }

  public async confirmStripePayment(payload): Promise<void> {
    await this.http.post(`${apiPayments}/confirm-stripe-payment`, payload).toPromise();
  }

  public setProduct(productId: number[], contentLanguage: Language = Language.en) {
    this.productIds = productId;
    this.contentLanguage = contentLanguage;
    this.productIdsSubject.next(this.productIds);
  }

  public getProductId() {
    return this.productIdsSubject.asObservable();
  }

  public setInterval(interval: { begin: Moment; end: Moment }) {
    this.interval = interval;
    if (!this.filterSubject) {
      this.filterSubject = new BehaviorSubject(interval);
    } else {
      this.filterSubject.next(interval);
    }
  }

  public updateAvailability() {
    this.filterSubject && this.filterSubject.next(this.interval);
  }

  public getInterval(): Observable<{ begin: Moment; end: Moment }> {
    return this.filterSubject ? this.filterSubject.asObservable() : null;
  }

  public getProducts(): Observable<ProductModel[]> {
    return this.productsSubject.asObservable();
  }
}
